import type {
  ParcelAttribution,
  LulcCoverage,
  CropCoverage,
  ForestCoverage,
  LulcClass,
  CropClass,
  ForestClass,
  LayerFeatureData,
} from "../types/agricultural-lulc";

interface RawLulcBreakdownItem {
  class: string;
  coverage_pct: number;
  coverage_area: number;
}

interface RawCropBreakdownItem {
  class: string;
  crop_name: string;
  coverage_pct: number;
  harvest_pct: number | null;
  harvest_area: number | null;
}

interface RawForestBreakdownItem {
  class: string;
  coverage_pct: number;
  coverage_area: number;
}

function parseLulcBreakdown(json: string | undefined): LulcCoverage[] {
  if (!json) return [];
  try {
    const items: RawLulcBreakdownItem[] = JSON.parse(json);
    return items.map((item) => ({
      land_use: item.class as LulcClass,
      coverage_area_sqm: item.coverage_area,
      coverage_pct: item.coverage_pct,
    }));
  } catch {
    return [];
  }
}

function parseCropBreakdown(json: string | undefined): CropCoverage[] {
  if (!json) return [];
  try {
    const items: RawCropBreakdownItem[] = JSON.parse(json);
    return items.map((item) => ({
      class_main: item.class,
      crop_name: (item.crop_name || item.class) as CropClass,
      coverage_pct: item.coverage_pct,
      harvest_pct: item.harvest_pct ?? 0,
    }));
  } catch {
    return [];
  }
}

function parseForestBreakdown(json: string | undefined): ForestCoverage[] {
  if (!json) return [];
  try {
    const items: RawForestBreakdownItem[] = JSON.parse(json);
    return items.map((item) => ({
      forest_class: item.class as ForestClass,
      coverage_pct: item.coverage_pct,
    }));
  } catch {
    return [];
  }
}

export function buildParcelAttribution(
  pid: string,
  areaSqm: number,
  layerData: LayerFeatureData,
): ParcelAttribution {
  const areaHa = Math.round((areaSqm / 10000) * 100) / 100;
  return {
    cadastre_polygon_pid: pid,
    area_sqm: areaSqm,
    area_ha: areaHa,
    lulc_2024: parseLulcBreakdown(layerData.lulc2024Breakdown),
    lulc_2025: parseLulcBreakdown(layerData.lulc2025Breakdown),
    crops_2025: parseCropBreakdown(layerData.cropsBreakdown),
    forest_2025: parseForestBreakdown(layerData.forestBreakdown),
  };
}
